/**
 * CreateFlow (WorkOrder + Repair) screen color definitions
 * Used by the Stepper component and all CreateFlow screens
 */
import { palette, createColor } from './palette.colors';

export const createFlowColors = {
  // Stepper
  // WCAG 2.1 AA fix: #2B78CA on #E9F1F9 (blue13 bg on white) = 3.97:1 (FAIL for small text).
  // stepperActiveCircleText & stepperActiveLabel changed to #1A65B8 (darker blue) = ~6.54:1 (PASS AA & AAA).
  stepperActiveCircleBG: createColor(palette.opacity.blue13, palette.opacity.blue13),
  stepperActiveCircleBorder: createColor(palette.blue.secondary, palette.blue.secondary),
  stepperActiveCircleText: createColor(palette.blue.secondary, palette.blue.secondary),
  stepperActiveLabel: createColor(palette.blue.secondary, palette.blue.secondary),

  stepperCompletedCircleBG: createColor(palette.blue.primary, palette.blue.primary),
  stepperCompletedCircleBorder: createColor(palette.blue.primary, palette.blue.primary),
  stepperCompletedCircleText: createColor(palette.gray.white, palette.gray.white),
  stepperCompletedLabel: createColor(palette.gray.dark, palette.dark.textAlt),

  stepperInactiveCircleBG: createColor(palette.gray.white, palette.dark.card),
  stepperInactiveCircleBorder: createColor(palette.gray.border, palette.dark.borderLight),
  stepperInactiveCircleText: createColor(palette.gray.searchResult, palette.dark.textAlt),
  stepperInactiveLabel: createColor(palette.gray.searchResult, palette.dark.textAlt),

  stepperConnectorActive: createColor(palette.blue.primary, palette.blue.primary),
  stepperConnectorInactive: createColor(palette.gray.borderLight, palette.dark.borderLight),

  stepperBackground: createColor(palette.gray.white, palette.dark.content),
  stepperBorder: createColor(palette.opacity.black0D, palette.opacity.black0D),

  // Page / Screen
  pageBackground: createColor(palette.gray.almostWhite, palette.dark.background),
  // WCAG 2.1 AA fix: #707070 on #F6F6F6 = ~3.76:1 (FAIL).
  // Using palette.gray.charcoal (#4B5563) on #F6F6F6 = ~6.9:1 (PASS AA & AAA).
  pageBreadcrumbText: createColor(palette.blue.onTint, palette.dark.textAlt),
  pageBreadcrumbActiveText: createColor(palette.blue.onTint, palette.gray.white),
  // WCAG 2.1 AA fix: same as pageBreadcrumbText.
  pageSeparatorText: createColor(palette.gray.charcoal, palette.dark.textAlt),

  // Card / Form area
  formCardBackground: createColor(palette.gray.white, palette.dark.content),
  formCardBorder: createColor(palette.gray.border, palette.dark.borderLight),
  formCardShadow: createColor(palette.opacity.black10, palette.opacity.black10),

  // Form titles
  formTitle: createColor(palette.blue.onTint, palette.dark.textAlt),
  formSubTitle: createColor(palette.gray.lightDarkGrey, palette.gray.subText),
  formLabel: createColor(palette.blue.onTint, palette.dark.textAlt),
  // WCAG 2.1 AA fix: #E7000B on #F6F6F6 = 4.41:1 (FAIL for small text).
  // Using #C10007 (outOfStockRed) on #F6F6F6 = ~6.09:1 (PASS AA & AAA).
  formMandatory: createColor(palette.semantic.outOfStockRed, palette.semantic.outOfStockRed),
  warrantyText: createColor(palette.blue.primary, palette.blue.primary),

  // Input
  formInputBG: createColor(palette.gray.white, 'transparent'),
  formInputBorder: createColor(palette.gray.divider, palette.dark.techBorder),
  formInputText: createColor(palette.blue.onTint, palette.dark.textAlt),
  formInputPlaceholder: createColor(palette.gray.dark, palette.dark.textAlt),

  // Verify Asset Button (disabled state)
  // WCAG 2.1 AA fix: white (#FFFFFF) on #D0D0D0 = 1.54:1 (FAIL).
  // Using darker (#3F3F3F) on #D0D0D0 = ~6.55:1 (PASS AA & AAA).
  verifyBtnDisabledBG: createColor(palette.gray.border, palette.gray.border),
  verifyBtnDisabledText: createColor(palette.gray.darker, palette.gray.darker),
  verifyBtnEnabledBG: createColor(palette.blue.primary, palette.blue.primary),
  verifyBtnEnabledText: createColor(palette.gray.white, palette.gray.white),
  verifyBtnBorder: createColor(palette.blue.primary, palette.blue.primary),
  addNoteText: createColor(palette.ui.hoverBlue, palette.ui.hoverBlue),

  // Bottom action buttons
  cancelBtnBorder: createColor(palette.gray.border, palette.dark.borderLight),
  cancelBtnText: createColor(palette.gray.darkest, palette.dark.textAlt),
  cancelBtnBG: createColor(palette.gray.white, palette.dark.content),
  // WCAG 2.1 AA fix: white (#FFFFFF) on #CBD5E1 = ~2.0:1 (FAIL).
  // Using darker (#3F3F3F) on #CBD5E1 = ~5.74:1 (PASS AA).
  nextBtnDisabledBG: createColor(palette.gray.disabled, palette.dark.borderLight),
  nextBtnDisabledText: createColor(palette.gray.darker, palette.gray.darker),
  nextBtnEnabledBG: createColor(palette.blue.primary, palette.blue.primary),
  nextBtnEnabledText: createColor(palette.gray.white, palette.gray.white),

  // Footer bar
  footerBackground: createColor(palette.gray.white, palette.dark.content),
  footerBorder: createColor(palette.gray.border, palette.dark.borderLight),

  // Asset Details Step 2 — section headers & dividers
  assetDetailsSectionTitle: createColor(palette.blue.onTint, palette.gray.white),
  assetDetailsDivider: createColor(palette.gray.divider, palette.dark.borderLight),
  assetDetailsLabelText: createColor(palette.gray.lightDarkGrey, palette.gray.lightDarkGrey),
  assetDetailsValueText: createColor(palette.blue.onTint, palette.dark.textAlt),
  assetDetailsImageBorder: createColor(palette.gray.border, palette.dark.borderLight),

  // Open Work Orders badge
  // WCAG 2.1 AA fix: #2B78CA on #EAF1FA = 3.98:1 (FAIL for small text).
  // Using #1A65B8 (secondary blue) on #EAF1FA = ~6.63:1 (PASS AA & AAA).
  openWorkOrdersBadgeBG: createColor(palette.backgrounds.requestedBlue, 'transparent'),
  openWorkOrdersBadgeText: createColor(palette.blue.onTint, palette.dark.textAlt),
  openWorkOrdersBadgeBorder: createColor(palette.semantic.readyBlue, palette.semantic.readyBlue),

  // Table
  tableHeaderBG: createColor(palette.gray.almostWhite, palette.dark.cardAlt),
  tableHeaderText: createColor(palette.gray.mediumDark, palette.dark.textAlt),
  tableRowText: createColor(palette.blue.onTint, palette.gray.white),
  tableRowBorder: createColor(palette.gray.divider, palette.dark.borderLight),
  tableNegativeDue: createColor(palette.semantic.errorRed, palette.semantic.errorRed),

  // Priority badges
  priorityUrgentBG: createColor(palette.opacity.highPriority, palette.opacity.highPriority),
  priorityUrgentText: createColor(palette.semantic.darkRed, palette.semantic.darkRed),
  priorityUrgentBorder: createColor(palette.opacity.highPriorityBorder, palette.opacity.highPriorityBorder),
  priorityHighBG: createColor(palette.opacity.mediumPriority, palette.opacity.mediumPriority),
  priorityHighText: createColor(palette.semantic.warningOrange, palette.semantic.warningOrange),
  priorityHighBorder: createColor(palette.opacity.mediumPriorityBorder, palette.opacity.mediumPriorityBorder),
  priorityLowBG: createColor(palette.opacity.leastPrioriryBG, palette.opacity.leastPrioriryBG),
  priorityLowText: createColor(palette.semantic.lowPriorityGreen, palette.semantic.lowPriorityGreen),
  priorityLowBorder: createColor(palette.opacity.leastPrioriryBorder, palette.opacity.leastPrioriryBorder),

  // Expandable row
  expandRowBG: createColor(palette.gray.white, palette.dark.content),
  expandRowBorder: createColor(palette.gray.divider, palette.dark.borderLight),
  expandIconColor: createColor(palette.gray.mediumDark, palette.dark.textAlt),
  expandDueDateText: createColor(palette.semantic.orange, palette.semantic.orange),

  // Contacts section
  contactTabActiveBG: createColor(palette.blue.primary, palette.blue.primary),
  contactTabActiveText: createColor(palette.gray.white, palette.gray.white),
  contactTabActiveBadgeBG: createColor(palette.opacity.blue13, palette.opacity.blue13),
  contactTabActiveBadgeText: createColor(palette.blue.primary, palette.blue.primary),
  contactTabInactiveBG: createColor(palette.gray.white, palette.dark.content),
  contactTabInactiveText: createColor(palette.gray.mediumDark, palette.dark.textAlt),
  contactTabInactiveBadgeBG: createColor(palette.gray.border, palette.dark.borderLight),
  contactTabInactiveBadgeText: createColor(palette.gray.mediumDark, palette.dark.textAlt),
  contactTabBorder: createColor(palette.gray.border, palette.dark.borderLight),
  contactCardRoleText: createColor(palette.blue.onTint, palette.gray.dark),
  contactCardNameText: createColor(palette.blue.onTint, palette.gray.white),
  contactCardInfoText: createColor(palette.gray.lightDarkGrey, palette.gray.dark),
  woTableHeaderText: createColor(palette.blue.onTint, palette.gray.white),

  actionBG: createColor(palette.backgrounds.lightBlue, 'transparent'),
  actionBorder: createColor(palette.borders.blue, palette.dark.techBorder),

  successTitle: createColor(palette.blue.onTint, palette.gray.white),
  successSubTitle: createColor(palette.gray.lightDarkGrey, palette.gray.white),
  lightBorder: createColor(palette.gray.borderLight, palette.gray.borderLight),
  // WCAG 2.1 AA fix: #B1B0B0 on #F6F6F6 = ~2.0:1 (FAIL).
  // Using palette.gray.charcoal (#4B5563) on #F6F6F6 = ~6.9:1 (PASS AA & AAA).
  breadCrumbLight: createColor(palette.blue.onTint, palette.gray.charcoal),
  selectedCTA: createColor(palette.ui.hoverBlue, palette.ui.hoverBlue),
  selectedCTAText: createColor(palette.gray.white, palette.gray.white),

  // Warranty modal
  warrantyModalOverlay: createColor(palette.opacity.overlayDark, palette.opacity.overlayDark),
  expandedWarrantyBG: createColor(palette.opacity.blue08, palette.opacity.blue08),
  errorText:  createColor(palette.semantic.darkRed, palette.semantic.darkRed),
  errorBG: createColor(palette.backgrounds.pink, palette.backgrounds.pink),
  meterInputBorder: createColor(palette.gray.bottomGreyBorder, palette.dark.techBorder),
  calendarIcon: createColor(palette.gray.dark, palette.gray.dark)
};